import { z } from "zod";
import { expenseEventSchema, posCashMovementEventSchema, posShiftClosedEventSchema } from "./accounting.js";
import { invoiceIssuedEventSchema, paymentApprovedEventSchema, paymentRequestEventSchema, subscriptionChangedEventSchema, } from "./billing.js";
import { productDeletedEventSchema } from "./catalog-bin.js";
import { priceChangedEventSchema } from "./catalog-prices.js";
import { productSchema } from "./catalog-products.js";
import { productStorefrontChangedEventSchema } from "./catalog-storefront.js";
import { payoutSchema } from "./cod.js";
import { courierStatusSchema, shipmentSchema } from "./couriers.js";
import { creditPostedEventSchema, customerEventSchema, customerMergedEventSchema } from "./customers.js";
import { entitlementThresholdEventSchema } from "./entitlements.js";
import { importJobFinishedEventSchema } from "./imports.js";
import { stockChangedEventSchema, stockNegativeEventSchema } from "./inventory.js";
import { locationSchema, registerSchema } from "./locations.js";
import { orderEventSchema, orderPaymentChangedEventSchema } from "./orders.js";
import { paymentEventSchema } from "./payments.js";
import { goodsReceiptEventSchema, purchaseOrderEventSchema, purchaseReturnEventSchema } from "./purchases.js";
import { receiptlessReturnEventSchema, returnEventSchema } from "./returns.js";
import { tenantCreatedEventSchema } from "./signup.js";
import { stockCountApprovedEventSchema } from "./stock-counts.js";
import { supplierPaymentEventSchema } from "./suppliers.js";
import { deviceSyncStalledEventSchema } from "./sync/health.js";
import { transferEventSchema } from "./transfers.js";
/**
 * Domain events (docs/specs/events-jobs.md, ADR-0004). Every event travels in this envelope; `data` is validated
 * against the event's registered, versioned schema when it is published and again when it is handled.
 */
export const EVENT_TYPE_PATTERN = /^[a-z][a-z_]*\.[a-z][a-z_]*\.[a-z][a-z_]*$/;
export const eventActorSchema = z.object({
    type: z.enum(["user", "system", "device", "platform"]),
    id: z.uuid().nullable(),
});
export const eventEnvelopeSchema = z.object({
    id: z.uuid(),
    type: z.string().regex(EVENT_TYPE_PATTERN),
    version: z.number().int().min(1),
    tenantId: z.uuid(),
    occurredAt: z.iso.datetime(),
    actor: eventActorSchema,
    data: z.unknown(),
});
export function defineEvent(type, version, data) {
    if (!EVENT_TYPE_PATTERN.test(type))
        throw new Error(`Invalid event type ${JSON.stringify(type)}`);
    return { type, version, data };
}
// identity ---------------------------------------------------------------------------------------------------
const roleAssignmentData = z.object({
    assignmentId: z.uuid(),
    userId: z.uuid(),
    roleId: z.uuid(),
    locationId: z.uuid().nullable(),
});
export const identityRoleAssigned = defineEvent("identity.role.assigned", 1, roleAssignmentData);
export const identityRoleUnassigned = defineEvent("identity.role.unassigned", 1, roleAssignmentData);
const deviceData = z.object({
    deviceId: z.uuid(),
    locationId: z.uuid(),
    registerId: z.uuid(),
    deviceCode: z.string(),
});
/** A POS device was paired with a register (B-45). */
export const identityDevicePaired = defineEvent("identity.device.paired", 1, deviceData);
/** A POS device was revoked; it locks on next contact. */
export const identityDeviceRevoked = defineEvent("identity.device.revoked", 1, deviceData);
/** A user's POS PIN was set or reset in this tenant; devices get the new hash through the staff feed. */
export const identityStaffPinChanged = defineEvent("identity.staff_pin.changed", 1, z.object({ userId: z.uuid(), isSet: z.boolean() }));
// files -------------------------------------------------------------------------------------------------------
/** An upload passed its checks (size, type); images still need processing. */
export const filesFileConfirmed = defineEvent("files.file.confirmed", 1, z.object({ fileId: z.uuid(), purpose: z.string(), contentType: z.string() }));
/** A file is usable: images have their WebP original and variants. */
export const filesFileReady = defineEvent("files.file.ready", 1, z.object({ fileId: z.uuid(), purpose: z.string() }));
// locations ---------------------------------------------------------------------------------------------------
// The full row, so consumers (POS sync, search) never query back into the locations module (CLAUDE.md §10).
export const locationsLocationCreated = defineEvent("locations.location.created", 1, locationSchema);
export const locationsLocationUpdated = defineEvent("locations.location.updated", 1, locationSchema);
export const locationsLocationDeactivated = defineEvent("locations.location.deactivated", 1, locationSchema);
export const locationsLocationActivated = defineEvent("locations.location.activated", 1, locationSchema);
export const locationsRegisterCreated = defineEvent("locations.register.created", 1, registerSchema);
export const locationsRegisterUpdated = defineEvent("locations.register.updated", 1, registerSchema);
export const locationsRegisterDeactivated = defineEvent("locations.register.deactivated", 1, registerSchema);
export const locationsRegisterActivated = defineEvent("locations.register.activated", 1, registerSchema);
// catalog -----------------------------------------------------------------------------------------------------
// The whole product (with variants and barcodes): search (B-22) and the POS feed (B-41) need no read-back.
export const catalogProductCreated = defineEvent("catalog.product.created", 1, productSchema);
export const catalogProductUpdated = defineEvent("catalog.product.updated", 1, productSchema);
export const catalogProductArchived = defineEvent("catalog.product.archived", 1, productSchema);
export const catalogProductUnarchived = defineEvent("catalog.product.unarchived", 1, productSchema);
// B-80b: the Trash and deleting for good (docs/specs/catalog.md Part 4).
export const catalogProductTrashed = defineEvent("catalog.product.trashed", 1, productSchema);
export const catalogProductRestored = defineEvent("catalog.product.restored", 1, productSchema);
export const catalogProductDeleted = defineEvent("catalog.product.deleted", 1, productDeletedEventSchema);
export const catalogPriceChanged = defineEvent("catalog.price.changed", 1, priceChangedEventSchema);
export const catalogProductStorefrontChanged = defineEvent("catalog.product.storefront_changed", 1, productStorefrontChangedEventSchema);
// A category, brand or tag was renamed, moved or deleted: products using it are reindexed for search (B-22).
// A deleted tag's links are already gone (cascade), so `deleted` asks for a full reindex instead.
const referenceChanged = z.object({ id: z.uuid(), deleted: z.boolean() });
export const catalogCategoryChanged = defineEvent("catalog.category.changed", 1, referenceChanged);
export const catalogBrandChanged = defineEvent("catalog.brand.changed", 1, referenceChanged);
export const catalogTagChanged = defineEvent("catalog.tag.changed", 1, referenceChanged);
// imports ------------------------------------------------------------------------------------------------------
export const importsJobCompleted = defineEvent("imports.job.completed", 1, importJobFinishedEventSchema);
export const importsJobFailed = defineEvent("imports.job.failed", 1, importJobFinishedEventSchema);
// inventory --------------------------------------------------------------------------------------------------
// Levels after the change, so the POS feed, search and reports never query back into inventory (CLAUDE.md §10).
export const inventoryStockChanged = defineEvent("inventory.stock.changed", 1, stockChangedEventSchema);
export const inventoryStockNegative = defineEvent("inventory.stock.negative", 1, stockNegativeEventSchema);
// The transfer with its quantities after each step (B-26).
export const inventoryTransferRequested = defineEvent("inventory.transfer.requested", 1, transferEventSchema);
export const inventoryTransferApproved = defineEvent("inventory.transfer.approved", 1, transferEventSchema);
export const inventoryTransferDispatched = defineEvent("inventory.transfer.dispatched", 1, transferEventSchema);
export const inventoryTransferReceived = defineEvent("inventory.transfer.received", 1, transferEventSchema);
export const inventoryTransferClosed = defineEvent("inventory.transfer.closed", 1, transferEventSchema);
export const inventoryTransferCancelled = defineEvent("inventory.transfer.cancelled", 1, transferEventSchema);
// What a stock count posted (B-27).
export const inventoryCountApproved = defineEvent("inventory.count.approved", 1, stockCountApprovedEventSchema);
// purchasing ---------------------------------------------------------------------------------------------------
// Documents carry their totals and lines, so accounting and reports never read back (B-28).
export const purchasingPurchaseOrderSent = defineEvent("purchasing.purchase_order.sent", 1, purchaseOrderEventSchema);
export const purchasingPurchaseOrderClosed = defineEvent("purchasing.purchase_order.closed", 1, purchaseOrderEventSchema);
export const purchasingPurchaseOrderCancelled = defineEvent("purchasing.purchase_order.cancelled", 1, purchaseOrderEventSchema);
export const purchasingGoodsReceiptPosted = defineEvent("purchasing.goods_receipt.posted", 1, goodsReceiptEventSchema);
export const purchasingPurchaseReturnPosted = defineEvent("purchasing.purchase_return.posted", 1, purchaseReturnEventSchema);
export const purchasingSupplierPaymentRecorded = defineEvent("purchasing.supplier_payment.recorded", 1, supplierPaymentEventSchema);
export const purchasingSupplierPaymentReversed = defineEvent("purchasing.supplier_payment.reversed", 1, supplierPaymentEventSchema);
// customers --------------------------------------------------------------------------------------------------
export const customersCustomerCreated = defineEvent("customers.customer.created", 1, customerEventSchema);
export const customersCustomerUpdated = defineEvent("customers.customer.updated", 1, customerEventSchema);
export const customersCustomerMerged = defineEvent("customers.customer.merged", 1, customerMergedEventSchema);
// Every dues entry with the balance after it (B-30); sales and accounting read these, never the table.
export const customersCreditPosted = defineEvent("customers.credit.posted", 1, creditPostedEventSchema);
// sales ---------------------------------------------------------------------------------------------------------
/** One per order status step (docs/specs/sales.md); `from` is the status before. */
export const salesOrderCreated = defineEvent("sales.order.created", 1, orderEventSchema);
/** Edited before shipping or merged into (O-04, O-05). */
export const salesOrderUpdated = defineEvent("sales.order.updated", 1, orderEventSchema);
export const salesOrderConfirmed = defineEvent("sales.order.confirmed", 1, orderEventSchema);
export const salesOrderPacked = defineEvent("sales.order.packed", 1, orderEventSchema);
export const salesOrderShipped = defineEvent("sales.order.shipped", 1, orderEventSchema);
export const salesOrderDelivered = defineEvent("sales.order.delivered", 1, orderEventSchema);
export const salesOrderPartiallyDelivered = defineEvent("sales.order.partially_delivered", 1, orderEventSchema);
export const salesOrderReturned = defineEvent("sales.order.returned", 1, orderEventSchema);
export const salesOrderCancelled = defineEvent("sales.order.cancelled", 1, orderEventSchema);
/** O-07/O-08: a customer return or exchange was posted (stock already moved). */
export const salesReturnPosted = defineEvent("sales.return.posted", 1, returnEventSchema);
/** POS-07: a return without a receipt (no order) was posted at a counter (B-50). */
export const salesReceiptlessReturnPosted = defineEvent("sales.receiptless_return.posted", 1, receiptlessReturnEventSchema);
export const salesOrderPaymentChanged = defineEvent("sales.order.payment_changed", 1, orderPaymentChangedEventSchema);
// payments ------------------------------------------------------------------------------------------------------
/** Money into or out of an account (A-01); a reversal is a new payment with `reversalOfId`. */
export const paymentsPaymentRecorded = defineEvent("payments.payment.recorded", 1, paymentEventSchema);
export const paymentsPaymentVerified = defineEvent("payments.payment.verified", 1, paymentEventSchema);
/** An account was created or edited (name, status, location, register); POS sync re-reads it (B-42). */
export const paymentsAccountChanged = defineEvent("payments.account.changed", 1, z.object({ id: z.uuid(), locationId: z.uuid().nullable() }));
// platform ---------------------------------------------------------------------------------------------------------
/** Tenant settings changed (FND-13): the names of the changed fields. POS sync re-reads them (B-42). */
export const platformSettingsUpdated = defineEvent("platform.settings.updated", 1, z.object({ changed: z.array(z.string()) }));
// shipping ---------------------------------------------------------------------------------------------------------
/** A courier booking was queued; its job is enqueued only once the recording transaction commits (S-02). */
export const shippingShipmentRequested = defineEvent("shipping.shipment.requested", 1, z.object({ shipmentId: z.uuid() }));
/** The courier accepted the parcel (consignment and tracking code known). */
export const shippingShipmentBooked = defineEvent("shipping.shipment.booked", 1, shipmentSchema);
/** Every booking attempt failed; the shipment waits for a retry or a cancel. */
export const shippingShipmentFailed = defineEvent("shipping.shipment.failed", 1, shipmentSchema);
/**
 * B-38: the courier status moved (webhook or polling), never backwards and never twice for the same status. Sales
 * moves the order on; shipping texts the customer where switched on.
 */
export const shippingShipmentStatusChanged = defineEvent("shipping.shipment.status_changed", 1, z.object({
    shipmentId: z.uuid(),
    orderId: z.uuid(),
    orderNumber: z.string(),
    from: courierStatusSchema.nullable(),
    to: courierStatusSchema,
    raw: z.string(),
}));
/** S-05: a returned parcel was scanned in; the order is returned and its goods are back in stock. */
export const shippingShipmentRtoReceived = defineEvent("shipping.shipment.rto_received", 1, shipmentSchema);
/** S-04: a courier payout was posted: its money is recorded in a payment account and its parcels are settled. */
export const shippingPayoutPosted = defineEvent("shipping.payout.posted", 1, payoutSchema);
/** SA-08: a subscription invoice was issued (numbered, immutable). */
export const billingInvoiceIssued = defineEvent("billing.invoice.issued", 1, invoiceIssuedEventSchema);
/** SA-09: the owner asked for approval of a bKash or cash payment. */
export const billingPaymentRequestSubmitted = defineEvent("billing.payment_request.submitted", 1, paymentRequestEventSchema);
export const billingPaymentRequestRejected = defineEvent("billing.payment_request.rejected", 1, paymentRequestEventSchema);
/** SA-09: the platform admin approved a payment; the invoice is paid. */
export const billingPaymentApproved = defineEvent("billing.payment.approved", 1, paymentApprovedEventSchema);
/** SA-10: plan, cycle or period changed (B-62 dunning and B-63 metrics follow it). */
export const billingSubscriptionChanged = defineEvent("billing.subscription.changed", 1, subscriptionChangedEventSchema);
/** SA-01: a business signed up (one transaction with its owner, branch and trial). */
export const platformTenantCreated = defineEvent("platform.tenant.created", 1, tenantCreatedEventSchema);
/** SA-06: a limit reached 80 % or 100 % of the plan (once per limit value and period). */
export const platformEntitlementThresholdReached = defineEvent("platform.entitlement.threshold_reached", 1, entitlementThresholdEventSchema);
// pos --------------------------------------------------------------------------------------------------------------
/** PRD §10.5: a device is online but its bills have waited over 15 minutes (or a seq gap stayed open); B-51. */
export const posDeviceSyncStalled = defineEvent("pos.device.sync_stalled", 1, deviceSyncStalledEventSchema);
/** POS-08: cash put into or taken out of a drawer during a shift (accounting journals it, B-54). */
export const posCashMovementRecorded = defineEvent("pos.cash_movement.recorded", 1, posCashMovementEventSchema);
/** POS-02: a shift was closed with its blind count; accounting journals a non-zero variance (B-54). */
export const posShiftClosed = defineEvent("pos.shift.closed", 1, posShiftClosedEventSchema);
// accounting -------------------------------------------------------------------------------------------------------
/** A-02: an expense was paid from an account (its payment recorded in the same transaction). */
export const accountingExpenseRecorded = defineEvent("accounting.expense.recorded", 1, expenseEventSchema);
/** A-02: an expense was voided by a reversing entry. */
export const accountingExpenseVoided = defineEvent("accounting.expense.voided", 1, expenseEventSchema);
// notifications -----------------------------------------------------------------------------------------------
/** A tenant message was recorded; its delivery job is queued only once the recording transaction commits. */
export const notificationsMessageRequested = defineEvent("notifications.message.requested", 1, z.object({ messageId: z.uuid() }));
/** Every event the system publishes. Add new definitions here (contracts first, CLAUDE.md §6.7). */
export const EVENT_DEFINITIONS = [
    identityRoleAssigned,
    identityRoleUnassigned,
    identityDevicePaired,
    identityDeviceRevoked,
    identityStaffPinChanged,
    filesFileConfirmed,
    filesFileReady,
    notificationsMessageRequested,
    locationsLocationCreated,
    locationsLocationUpdated,
    locationsLocationDeactivated,
    locationsLocationActivated,
    locationsRegisterCreated,
    locationsRegisterUpdated,
    locationsRegisterDeactivated,
    locationsRegisterActivated,
    catalogProductCreated,
    catalogProductUpdated,
    catalogProductArchived,
    catalogProductUnarchived,
    catalogProductTrashed,
    catalogProductRestored,
    catalogProductDeleted,
    catalogPriceChanged,
    catalogProductStorefrontChanged,
    catalogCategoryChanged,
    catalogBrandChanged,
    catalogTagChanged,
    importsJobCompleted,
    importsJobFailed,
    inventoryStockChanged,
    inventoryStockNegative,
    inventoryTransferRequested,
    inventoryTransferApproved,
    inventoryTransferDispatched,
    inventoryTransferReceived,
    inventoryTransferClosed,
    inventoryTransferCancelled,
    inventoryCountApproved,
    purchasingPurchaseOrderSent,
    purchasingPurchaseOrderClosed,
    purchasingPurchaseOrderCancelled,
    purchasingGoodsReceiptPosted,
    purchasingPurchaseReturnPosted,
    purchasingSupplierPaymentRecorded,
    purchasingSupplierPaymentReversed,
    customersCustomerCreated,
    customersCustomerUpdated,
    customersCustomerMerged,
    customersCreditPosted,
    salesOrderCreated,
    salesOrderUpdated,
    salesOrderConfirmed,
    salesOrderPacked,
    salesOrderShipped,
    salesOrderDelivered,
    salesOrderPartiallyDelivered,
    salesOrderReturned,
    salesOrderCancelled,
    salesOrderPaymentChanged,
    salesReturnPosted,
    salesReceiptlessReturnPosted,
    paymentsPaymentRecorded,
    paymentsPaymentVerified,
    paymentsAccountChanged,
    platformSettingsUpdated,
    platformEntitlementThresholdReached,
    platformTenantCreated,
    billingInvoiceIssued,
    billingPaymentRequestSubmitted,
    billingPaymentRequestRejected,
    billingPaymentApproved,
    billingSubscriptionChanged,
    shippingShipmentRequested,
    shippingShipmentBooked,
    shippingShipmentFailed,
    shippingShipmentStatusChanged,
    shippingShipmentRtoReceived,
    shippingPayoutPosted,
    posDeviceSyncStalled,
    posCashMovementRecorded,
    posShiftClosed,
    accountingExpenseRecorded,
    accountingExpenseVoided,
];
/** Every job payload carries the tenant it runs in; jobs without it are rejected (CLAUDE.md §8). */
export const tenantJobPayloadSchema = z.object({ tenantId: z.uuid() });
/**
 * The one reviewed exception (docs/specs/notifications.md): user-level work that belongs to no tenant, such as
 * sign-in OTPs. Runs without tenant context; only processors declared with this schema accept it.
 */
export const platformJobPayloadSchema = z.object({ platform: z.literal(true) });
//# sourceMappingURL=events.js.map